import type { LessonUpdatePayload } from '../../model/lessons/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface LessonUpdateApiOptions {
  disciplineId: string | number
  lessonId: number
  payload: LessonUpdatePayload
  successTitle?: string
  successDescription?: string
  onStart?: () => void
  onFinish?: () => void
}

/** `PATCH /admin-api/disciplines/{discipline}/lessons/{booking}` — the edit form and the cancel modal. A refusal
 *  (completed lesson, busy tutor, no customer timezone) is shown as the API words it. */
export const useLessonUpdateApi = async (options: LessonUpdateApiOptions): Promise<boolean> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  const url = useAdminRoutes('admin.lessons.api.lesson', {
    discipline_id: options.disciplineId,
    lesson_id: options.lessonId,
  })

  options.onStart?.()

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return false
    }

    await $axios.patch<ApiResponse>(url, options.payload)

    toast.success({
      title: options.successTitle ?? 'Successfully updated!',
      description: options.successDescription ?? 'Selected lesson was successfully updated!',
    })

    return true
  } catch (err: unknown) {
    toast.error({
      title: 'Error on update!',
      description: getAxiosErrorMessage(err),
    })

    return false
  } finally {
    options.onFinish?.()
  }
}
